'use client';

import type { ProductCardProps } from '@petsmart-ui/sparky';
import { Grid, Heading, LayoutContainer, ProductCard, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { useSearchParams } from 'next/navigation';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';

import { getMappedBrandName, type ProductHit } from '@libs/algolia';
import {
    algoliaTrackClickEvent,
    dataLayerPush,
    Events,
    initAlgoliaTracking,
    PageSectionAnalyticsTracking,
    storageSearchedTermKey,
    trackProductTileClick,
    trackViewSearchResultsEvent,
    useAnalyticsContext,
} from '@libs/analytics';
import {
    decodeString,
    filterActiveAlgoliaPromotions,
    getAutoshipPromo,
    type Product,
    sortProductAlgoliaPromotions,
    useDecisions,
    useFeaturedBrand,
    useScrollToSection,
    useSearchPage,
    useUserSession,
} from '@libs/common-next';
import type { EligiblePromotion } from '@libs/common-types';

import { AutoshipBadge } from '@/components/AutoshipBadge';
import ClearFilters from '@/components/ClearFilters';
import FilterContainer from '@/components/FilterContainer';
import NoSearchResult from '@/components/NoSearchResult';
import type { ProductGridProps } from '@/components/ProductGrid';
import { usePlpAutoshipSubscriptionLookup } from '@/components/ProductGrid/model/usePlpAutoshipSubscriptionLookup';
import ProductGridSeo from '@/components/ProductGrid/ProductGridSeo';
import { formatCategoryHeading, pushEvent, trackPlpProductTileItemClick } from '@/components/ProductGrid/utils';
import ProductPagination from '@/components/ProductPagination';
import { CATEGORIES_ATTRIBUTE } from '@/components/SearchLayout/utils';
import PriceRangeProvider from '@/providers';
import { isAutoshipBadgeEligible } from '@/utils/isAutoshipBadgeEligible';

import '@/components/ProductGrid/ProductGrid.scss';

const filterAndSortAlgoliaPromotions = (promotions: EligiblePromotion[]) => {
    const filteredPromotions = filterActiveAlgoliaPromotions({ promotions });
    let sortedPromotions = [] as EligiblePromotion[];
    if (filteredPromotions?.length) {
        sortedPromotions = sortProductAlgoliaPromotions(filteredPromotions);
    }
    return sortedPromotions;
};

export default function ProductGrid({
    products,
    entryId,
    totalResults,
    queryid,
    forceCategoryH1,
    seoMetadata,
}: ProductGridProps) {
    const { storageUserSession: { isRegistered = false } = {} } = useUserSession();
    const scrollToSection = useScrollToSection();

    const { items } = useCurrentRefinements();
    const { setSearchStats, getSearchStats } = useAnalyticsContext();
    const isSearchPage = useSearchPage();
    const isFeaturedBrand = useFeaturedBrand();
    const params = useSearchParams();

    const [productsWithPromotions, setProductsWithPromotions] = useState<Product[]>(products);
    const [productIdsTracked, setProductIdsTracked] = useState<string[]>([]);
    const [productIdsToTrack, setProductIdsToTrack] = useState<Product[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [shouldGenerateSchema, setShouldGenerateSchema] = useState<boolean>(true);

    const currentCategories =
        items.filter((item: CurrentRefinementsConnectorParamsItem) => item.label === CATEGORIES_ATTRIBUTE)[0] ?? null;
    const noResultWithFilters = items.length > 0 && products.length === 0;
    const noResult = items.length === 0 && products.length === 0;
    const searchQuery = params?.get('q');
    const searchTerm = params?.get('sterm');
    const searchType = params?.get('st') ?? 'manual';
    const searchAssist = params?.get('sa');
    const searchAssistTerm = params?.get('sat');

    const searchIsLoaded = useRef<boolean>(false);
    // because Algolia causes a lot of re-rendering, we need to track what products have been sent to data layer so we do not duplicate them
    const onProductTracked = (id: string) => {
        setProductIdsTracked(current => [...current, `${id}_${queryid}`]);
    };

    const onProductIntersect = (product: Product) => {
        if (
            !productIdsTracked.includes(`${product.id}_${queryid}`) &&
            products.map((p: Product) => p.id).includes(product.id)
        ) {
            setProductIdsToTrack(current => [...current, product]);
            onProductTracked(`${product.id}`);
        }
    };
    useEffect(() => {
        void initAlgoliaTracking();
    }, []);

    const optimizeProductImage = (src?: string) => {
        const baseImagesURL = 'https://s7d2.scene7.com/is/image/PetSmart';
        if (src?.includes(baseImagesURL)) {
            const base = src.split('?');
            const existingParams = base.slice(1);
            const joiner = existingParams?.length ? '&' : '';
            return [base[0], '?', ...existingParams, joiner, 'fmt=webp&qlt=80'].join('');
        }
        return undefined;
    };

    const decision = useDecisions('exp_as_class');
    const {
        isExistingAutoshipSubscriber,
        isPlpAutoshipBadgeEnabled,
        isPlpAutoshipCopyEnabled,
        isPerSkuSubscribedIndicatorEnabled,
        isSubscribedForTileSku,
        isAutoshipSubscriptionsLoading,
    } = usePlpAutoshipSubscriptionLookup();

    const viewItemListAutoshipContext = {
        isPlpAutoshipBadgeEnabled,
        isPerSkuSubscribedIndicatorEnabled,
        isSubscribedForTileSku,
    };

    const trackProductClick = (product: ProductHit) => {
        trackPlpProductTileItemClick(product, {
            isSearchPage,
            searchTerm: getSearchStats()?.searchTerm,
            viewItemListAutoshipContext,
        });
    };

    const getClassName = (eligiblePromotions: EligiblePromotion[] = []) => {
        if (!decision) return '';
        const promos = filterAndSortAlgoliaPromotions(eligiblePromotions);

        return promos.reduceRight((className, promo) => {
            return promo.type ? promo.type + promo.discountDisplay : className;
        }, '');
    };

    useEffect(() => {
        const term = searchQuery?.trim() || searchTerm?.trim();

        setSearchStats({
            searchTerm: term && term !== '' ? term : getSearchStats()?.searchTerm || '',
            searchResults: totalResults,
            searchType,
        });
    }, [searchQuery, searchTerm, totalResults, searchType, setSearchStats, getSearchStats]);

    useEffect(() => {
        if (productIdsToTrack.length === 0 || isAutoshipSubscriptionsLoading) {
            return;
        }

        const sortedProducts = [...productIdsToTrack].sort((a, b) => a.__position - b.__position);
        pushEvent(
            sortedProducts,
            isSearchPage,
            currentCategories,
            searchQuery ?? undefined,
            viewItemListAutoshipContext
        );
        setProductIdsToTrack([]);
    }, [productIdsToTrack, isAutoshipSubscriptionsLoading]);

    useEffect(() => {
        setProductsWithPromotions(products);
    }, [products, isRegistered]);

    useLayoutEffect(() => {
        const { hash } = window.location;
        // #tabs-1 is added to the url by contentful links
        // if it exists in the url, scroll to product-grid
        if (hash === '#tabs-1') {
            scrollToSection('product-grid', 'instant');
        }

        if (!searchQuery || searchIsLoaded.current) {
            return;
        }

        const isManualSearch = searchType === 'manual';

        const term = isManualSearch ? searchQuery : (searchTerm?.trim() ?? '');
        trackViewSearchResultsEvent(
            term,
            totalResults,
            searchType?.trim(),
            searchAssist as string,
            searchAssistTerm as string
        );
        searchIsLoaded.current = true;
    }, []);

    useEffect(() => {
        setShouldGenerateSchema(Boolean(products?.length && !isSearchPage));
    }, [isSearchPage, products?.length]);

    /**
     * SB-3454
     * https://petsmart.atlassian.net/browse/SB-3454
     *
     * `item_search_term` is persisted in sessionStorage so downstream ecommerce
     * events (view_item, add_to_cart, purchase, etc.) can be attributed back to
     * the originating search query.
     *
     * The search term should only remain associated with explicit search-driven
     * interactions.
     *
     * This effect listens for document clicks and removes the stored search term
     * only when the user clicks a link outside `#product-grid`.
     *
     * Product clicks inside the grid intentionally preserve the search term so
     * search attribution continues through PDP, cart, checkout, and purchase flows.
     *
     * Non-link clicks outside the grid are ignored because they do not necessarily
     * indicate navigation away from the search experience.
     */
    useEffect(() => {
        const productGrid = containerRef.current;

        if (!productGrid) {
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as Element | null;

            if (!target) {
                return;
            }

            const linkElement = target.closest('a');

            if (!linkElement) {
                return;
            }

            const clickedInsideProductGrid = productGrid.contains(target);

            if (clickedInsideProductGrid) {
                return;
            }

            sessionStorage.removeItem(storageSearchedTermKey);
        };

        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);

    const onClickTracker = (rawProductHit: ProductHit) => {
        if (rawProductHit) {
            trackProductTileClick({
                list: isSearchPage ? 'Search Results' : 'PLP Page',
                product: rawProductHit,
                variant: 'traditional',
                position: rawProductHit.__position,
            });
            algoliaTrackClickEvent(rawProductHit);
        }

        trackProductClick(rawProductHit);
    };

    return (
        <div id="product-grid" ref={containerRef}>
            <LayoutContainer className="product-grid__container">
                <PriceRangeProvider>
                    <FilterContainer maxRateAvailable={0} pageId={entryId ?? ''} />
                    <ClearFilters hasHeader={false} />
                </PriceRangeProvider>

                {(searchQuery || entryId) && products.length > 0 && (
                    <div className="result-text-container">
                        <Heading
                            data-testid="product-grid__heading"
                            className={cn('product-grid__heading', isFeaturedBrand && 'product-grid__heading-brands')}
                            tagName={forceCategoryH1 ? 'h1' : 'h2'}
                            size="headline"
                        >
                            {searchQuery
                                ? `Showing results for “${decodeString(searchQuery)}”`
                                : `${getMappedBrandName(formatCategoryHeading(decodeURIComponent(entryId ?? '')))}`}
                        </Heading>
                        <span className="product-grid__results-count">
                            ({searchQuery ? `${totalResults}` : `${totalResults} results`})
                        </span>
                    </div>
                )}

                {noResultWithFilters && <NoSearchResult useFilterMessage />}
                {noResult && <NoSearchResult useFilterMessage={false} />}
                {!noResultWithFilters && !noResult ? (
                    <>
                        <Grid className="product-grid" variant="4up" divider gap="md">
                            {productsWithPromotions.map(
                                ({
                                    id,
                                    objectID,
                                    outOfStock,
                                    rawProductHit,
                                    eligiblePromotions,
                                    showOptionsFrom,
                                    ...product
                                }) => {
                                    const isAutoshipEligible = isAutoshipBadgeEligible(rawProductHit);
                                    const autoshipPromo = isAutoshipEligible
                                        ? getAutoshipPromo(eligiblePromotions)
                                        : null;
                                    const isCustomerSubscribedToTileSku = isSubscribedForTileSku(
                                        String(rawProductHit.sku ?? rawProductHit.id)
                                    );

                                    return (
                                        <Grid.Item
                                            data-productid={id} // We use this one to create the PLP Surrogate-Key response header
                                            data-testid={`sparky-grid-item-${id}`}
                                            key={id || objectID}
                                            onClick={e => e.preventDefault()}
                                        >
                                            <PageSectionAnalyticsTracking
                                                sectionId="product-card"
                                                eventCallback={() => {
                                                    dataLayerPush({
                                                        event: Events.VIEW_ITEM_LIST,
                                                    });
                                                }}
                                                enabled={false}
                                                OnIntersect={() => {
                                                    onProductIntersect({
                                                        id,
                                                        objectID,
                                                        ...product,
                                                        rawProductHit,
                                                    });
                                                }}
                                            >
                                                <ProductCard
                                                    {...(product as ProductCardProps)}
                                                    imgSrc={optimizeProductImage(product.imgSrc)}
                                                    rating={product.rating || 0}
                                                    orientation="responsive"
                                                    data-testid="product-card"
                                                    data-productprices={product.prices?.join(',')}
                                                    onClick={() => {
                                                        onClickTracker(rawProductHit);
                                                    }}
                                                    {...(eligiblePromotions?.length && {
                                                        promos: filterAndSortAlgoliaPromotions(eligiblePromotions)
                                                            .filter(
                                                                ({ type }) =>
                                                                    type !== 'autoship' ||
                                                                    (isPlpAutoshipCopyEnabled && isAutoshipEligible)
                                                            )
                                                            .map(({ promoText }) => ({
                                                                description: promoText,
                                                            })),
                                                    })}
                                                    size="lg"
                                                    body={
                                                        isPlpAutoshipBadgeEnabled &&
                                                        isAutoshipEligible && (
                                                            <AutoshipBadge
                                                                isLoading={isAutoshipSubscriptionsLoading}
                                                                isExistingAutoshipSubscriber={
                                                                    isExistingAutoshipSubscriber
                                                                }
                                                                discountDisplay={autoshipPromo?.discountDisplay}
                                                                isPerSkuSubscribedIndicatorEnabled={
                                                                    isPerSkuSubscribedIndicatorEnabled
                                                                }
                                                                isCustomerSubscribedToTileSku={
                                                                    isCustomerSubscribedToTileSku
                                                                }
                                                            />
                                                        )
                                                    }
                                                    footer={
                                                        outOfStock ? (
                                                            <TextPassage
                                                                data-testid="sparky-card-out-of-stock"
                                                                size="sm"
                                                                className="product-grid__card-footer--out-of-stock"
                                                            >
                                                                Out of stock
                                                            </TextPassage>
                                                        ) : null
                                                    }
                                                    priceInfo={showOptionsFrom ? 'Options from' : undefined}
                                                    className={getClassName(eligiblePromotions)}
                                                />
                                            </PageSectionAnalyticsTracking>
                                        </Grid.Item>
                                    );
                                }
                            )}
                        </Grid>
                        <div data-search-interaction="true">
                            <ProductPagination containerRef={containerRef as RefObject<HTMLDivElement>} />
                        </div>
                    </>
                ) : null}
            </LayoutContainer>

            {shouldGenerateSchema && <ProductGridSeo seoMetadata={seoMetadata} products={products} />}
        </div>
    );
}
